import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { Activity, BarChart3, Briefcase, Download, FileText, Lock, RefreshCw, Shield, Signal, Store, ToggleLeft } from "lucide-react";
import "./styles.css";

type Page = "dashboard" | "merchants" | "subscriptions" | "segments" | "sync" | "exports" | "webhooks" | "activity" | "privacy" | "signals" | "features" | "settings";

const pages: Array<{ key: Page; label: string; icon: React.ComponentType<{ size?: number }> }> = [
  { key: "dashboard", label: "Dashboard", icon: BarChart3 },
  { key: "merchants", label: "Merchants", icon: Store },
  { key: "subscriptions", label: "Subscriptions", icon: Briefcase },
  { key: "segments", label: "Segment Health", icon: Activity },
  { key: "sync", label: "Sync Jobs", icon: RefreshCw },
  { key: "exports", label: "Exports", icon: Download },
  { key: "webhooks", label: "Webhooks", icon: Signal },
  { key: "activity", label: "Activity", icon: FileText },
  { key: "privacy", label: "Privacy/PCD", icon: Shield },
  { key: "signals", label: "App Review Signals", icon: Signal },
  { key: "features", label: "Features", icon: ToggleLeft },
  { key: "settings", label: "Settings", icon: Lock }
];

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  const res = await fetch(path, { ...init, headers });
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(body.error || `Request failed: ${res.status}`);
  return body;
}

function App() {
  const [session, setSession] = useState<boolean | null>(null);
  const [page, setPage] = useState<Page>("dashboard");
  useEffect(() => {
    api<{ ok: boolean }>("/api/session").then((data) => setSession(data.ok)).catch(() => setSession(false));
  }, []);
  if (session === null) return <div className="center">Loading owner session...</div>;
  if (!session) return <Login onLogin={() => setSession(true)} />;
  return (
    <div className="shell">
      <aside>
        <div className="brand">ReactivateIQ Admin</div>
        {pages.map((item) => {
          const Icon = item.icon;
          return (
            <button key={item.key} className={page === item.key ? "active" : ""} onClick={() => setPage(item.key)}>
              <Icon size={18} />
              {item.label}
            </button>
          );
        })}
      </aside>
      <main>
        <header>
          <h1>{pages.find((item) => item.key === page)?.label}</h1>
          <button onClick={() => api("/api/logout", { method: "POST" }).then(() => setSession(false))}>Log out</button>
        </header>
        {page === "dashboard" && <Dashboard />}
        {page === "merchants" && <Merchants />}
        {page === "subscriptions" && <TablePage path="/api/shops" columns={["domain", "plan_handle", "billing_status", "installed_at", "pcd_status"]} />}
        {page === "segments" && <TablePage path="/api/segment-health" columns={["shop_id", "segment_key", "customers_count", "export_eligible_count", "suppressed_count", "calculated_at"]} />}
        {page === "sync" && <TablePage path="/api/sync-jobs" columns={["shop_id", "type", "status", "progress_percent", "created_at", "completed_at", "error_message"]} />}
        {page === "exports" && <TablePage path="/api/exports" columns={["shop_id", "status", "platform_format", "row_count", "excluded_count", "expires_at", "created_at"]} />}
        {page === "webhooks" && <TablePage path="/api/webhooks" columns={["shop_id", "topic", "status", "received_at", "processed_at", "error_message"]} />}
        {page === "activity" && <TablePage path="/api/activity" columns={["shop_id", "level", "event", "message", "created_at"]} />}
        {page === "privacy" && <TablePage path="/api/privacy" columns={["domain", "pcd_status", "pcd_level2_email_approved", "read_all_orders_approved", "data_purge_requested_at", "data_purged_at"]} />}
        {page === "signals" && <TablePage path="/api/review-signals" columns={["shop_id", "signal", "status", "path", "created_at"]} />}
        {page === "features" && <StaticPanel title="Feature flags" text="MVP feature flags are code-owned: direct campaign integrations, writeback, discounts, SMS export, and product/category segmentation remain disabled." />}
        {page === "settings" && <StaticPanel title="Admin privacy posture" text="Owner admin shows aggregate operational metadata only. It does not expose customer emails, phones, names, addresses, raw order data, tokens, webhook bodies, export files, or raw customer payloads." />}
      </main>
    </div>
  );
}

function Login({ onLogin }: { onLogin: () => void }) {
  const [username, setUsername] = useState("owner");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    try {
      await api("/api/login", { method: "POST", body: JSON.stringify({ username, password }) });
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    }
  }
  return (
    <div className="login">
      <form onSubmit={submit}>
        <div className="logo">RIQ</div>
        <h1>Owner admin</h1>
        <label>Username<input value={username} onChange={(event) => setUsername(event.target.value)} /></label>
        <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        {error && <p className="error">{error}</p>}
        <button>Log in</button>
      </form>
    </div>
  );
}

function Dashboard() {
  const data = useData<{ metrics: Record<string, number> }>("/api/overview");
  const metrics = data.data?.metrics ?? {};
  return (
    <State data={data}>
      <section className="cards">
        {Object.entries(metrics).map(([key, value]) => (
          <article className="card" key={key}>
            <span>{key.replace(/_/g, " ")}</span>
            <strong>{String(value)}</strong>
          </article>
        ))}
      </section>
    </State>
  );
}

function Merchants() {
  const [selected, setSelected] = useState<string | null>(null);
  const data = useData<{ rows: Array<Record<string, unknown>> }>("/api/shops");
  return (
    <State data={data}>
      <Panel title="Merchants">
        <Table rows={data.data?.rows ?? []} columns={["shop_handle", "domain", "billing_status", "pcd_status", "pcd_level2_email_approved", "read_all_orders_approved", "latest_analysis", "dormant_customers", "export_eligible_customers"]} onSelect={(row) => setSelected(String(row.id))} />
      </Panel>
      {selected && <MerchantDrawer id={selected} onClose={() => setSelected(null)} />}
    </State>
  );
}

function MerchantDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const data = useData<Record<string, unknown>>(`/api/shops/${encodeURIComponent(id)}`);
  const sections = ["sync", "exports", "segments", "activity", "signals"] as const;
  return (
    <div className="drawer">
      <div className="drawerHead"><h2>Merchant detail</h2><button onClick={onClose}>Close</button></div>
      <State data={data}>
        <pre>{JSON.stringify(data.data?.shop ?? {}, null, 2)}</pre>
        {sections.map((section) => (
          <Panel key={section} title={section}>
            <Table rows={(data.data?.[section] as Array<Record<string, unknown>>) ?? []} columns={Object.keys(((data.data?.[section] as Array<Record<string, unknown>>) ?? [])[0] ?? {})} />
          </Panel>
        ))}
      </State>
    </div>
  );
}

function TablePage({ path, columns }: { path: string; columns: string[] }) {
  const data = useData<{ rows: Array<Record<string, unknown>> }>(path);
  return <State data={data}><Panel title="Rows"><Table rows={data.data?.rows ?? []} columns={columns} /></Panel></State>;
}

function StaticPanel({ title, text }: { title: string; text: string }) {
  return <Panel title={title}><p className="muted">{text}</p></Panel>;
}

function useData<T>(path: string) {
  const [state, setState] = useState<{ loading: boolean; data: T | null; error: string | null }>({ loading: true, data: null, error: null });
  useEffect(() => {
    let cancelled = false;
    api<T>(path).then((data) => !cancelled && setState({ loading: false, data, error: null })).catch((error: Error) => !cancelled && setState({ loading: false, data: null, error: error.message }));
    return () => { cancelled = true; };
  }, [path]);
  return state;
}

function State<T>({ data, children }: { data: { loading: boolean; error: string | null; data: T | null }; children: React.ReactNode }) {
  if (data.loading) return <div className="empty">Loading...</div>;
  if (data.error) return <div className="empty error">{data.error}</div>;
  return <>{children}</>;
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="panel"><h2>{title}</h2>{children}</section>;
}

function Table({ rows, columns, onSelect }: { rows: Array<Record<string, unknown>>; columns: string[]; onSelect?: (row: Record<string, unknown>) => void }) {
  if (!rows.length) return <div className="empty">No rows.</div>;
  return (
    <div className="tableWrap"><table><thead><tr>{columns.map((col) => <th key={col}>{col.replace(/_/g, " ")}</th>)}</tr></thead><tbody>
      {rows.map((row, index) => <tr key={String(row.id ?? index)} onClick={() => onSelect?.(row)} className={onSelect ? "clickable" : ""}>{columns.map((col) => <td key={col}>{String(row[col] ?? "-")}</td>)}</tr>)}
    </tbody></table></div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);

